import { BillingClosureBadges } from "../../components/BillingClosureBadges";
import { billingClosureState } from "../../utils/billingClosureState";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  GridToolbarContainer,
  GridToolbarQuickFilter,
  itIT,
} from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import LinearProgress from "@mui/material/LinearProgress";
import { toast } from "react-toastify";
import api from "../../api/api";
import { CustomFooterPersonalized } from "../../components/DataGrid/CustomFooterPersonalized";
import Modal from "../../components/Modal";
import { EXPORT_BUTTON_CLASS } from "../../utils/exportButtonStyle";
import { TotaliPrestazioniModal } from "../../components/TotaliPrestazioniModal";
import { TotalSectionCard } from "../../components/TotalSectionCard";
import { formatItalianNumber } from "../../utils/formatItalianNumber";

const euro = (value) => `${formatItalianNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\u00a0€`;

const dataIt = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const [anno, mese, giorno] = raw.split("-");
  return giorno && mese && anno ? `${giorno}/${mese}/${anno}` : "—";
};

const DEFAULT_F24_TIERS = [
  { min_cedolini: 0, max_cedolini: 5, importo: 120 },
  { min_cedolini: 6, max_cedolini: 15, importo: 240 },
  { min_cedolini: 16, max_cedolini: 50, importo: 360 },
  { min_cedolini: 51, max_cedolini: null, importo: 480 },
];

const f24Tier = (value, fasce = DEFAULT_F24_TIERS) => {
  const dipendenti = Math.max(0, Number(value) || 0);
  const item = fasce.find((f) => dipendenti >= Number(f.min_cedolini)
    && (f.max_cedolini == null || dipendenti <= Number(f.max_cedolini))) || fasce[fasce.length - 1];
  return { fascia: item.max_cedolini == null ? `Da ${item.min_cedolini}`
    : Number(item.min_cedolini) === 0 ? `Fino a ${item.max_cedolini}` : `${item.min_cedolini}-${item.max_cedolini}`,
  importo: Number(item.importo) };
};

const Toolbar = () => (
  <GridToolbarContainer>
    <GridToolbarQuickFilter placeholder="Cerca cliente o codice..." />
  </GridToolbarContainer>
);

const addRowIds = (rows, prefix) => (rows || []).map((row, index) => ({
  ...row,
  _id: `${prefix}_${row.id || row.codice_univoco_azienda}_${index}`,
}));

const codiceF24 = (row) => row?.tipologia_fatturazione === "B" ? "1430" : "1204";

export const ForfaitF24 = ({ initialMonth, livePeriodo, embedded = false, roleReadOnly = false }) => {
  const selected = /^\d{4}-(0[1-9]|1[0-2])$/.test(initialMonth || "")
    ? initialMonth
    : new Date().toISOString().slice(0, 7);
  const [anno, mese] = selected.split("-").map(Number);
  const [gennaio, setGennaio] = useState([]);
  const [prorata, setProrata] = useState([]);
  const [incarichiAnno, setIncarichiAnno] = useState([]);
  const [mostraZero, setMostraZero] = useState(false);
  const [soloProrata, setSoloProrata] = useState(false);
  const [tipologiaFiltro, setTipologiaFiltro] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [prezzo, setPrezzo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [storicoFasce, setStoricoFasce] = useState([]);
  const [loadingStorico, setLoadingStorico] = useState(false);
  const [fasceF24, setFasceF24] = useState(DEFAULT_F24_TIERS);
  const [fasceEditor, setFasceEditor] = useState(null);
  const [exportRange, setExportRange] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [totaliOpen, setTotaliOpen] = useState(false);
  const closure = billingClosureState(livePeriodo, 'forfait_f24');
  const prezzoClosed = prezzo?.tipologia_fatturazione === 'B' ? closure.bpointClosed : closure.ziClosed;

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const [incarichi, righePeriodo] = await Promise.all([
        api.get("/billing/forfait-f24/senza-forfait", { params: { anno, mese }, signal }),
        api.get("/billing/forfait-f24/clienti", { params: { anno, mese }, signal }),
      ]);
      setGennaio(mese === 1 ? addRowIds(righePeriodo.data, "gennaio") : []);
      setIncarichiAnno(incarichi.data || []);
      setProrata(mese === 1
        ? []
        : addRowIds(righePeriodo.data || [], "prorata"));
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      toast.error(error.response?.data?.message || "Errore caricamento forfait F24");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [anno, mese]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const loadFasce = useCallback(async () => {
    try {
      const response = await api.get('/billing/forfait-f24-fasce', {
        params: { data: `${anno}-${String(mese).padStart(2, '0')}-01` },
      });
      setFasceF24(response.data?.length ? response.data : DEFAULT_F24_TIERS);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Errore caricamento fasce F24');
    }
  }, [anno, mese]);

  useEffect(() => { loadFasce(); }, [loadFasce]);

  const openFasceEditor = () => setFasceEditor({
    decorrenza: `${anno}-${String(mese).padStart(2, '0')}-01`,
    fasce: fasceF24.map((f) => ({ min_cedolini: Number(f.min_cedolini),
      max_cedolini: f.max_cedolini == null ? '' : Number(f.max_cedolini), importo: Number(f.importo) })),
  });

  const salvaFasce = async () => {
    setSaving(true);
    try {
      const response = await api.put('/billing/forfait-f24-fasce', fasceEditor);
      setFasceF24(response.data || []);
      setFasceEditor(null);
      toast.success('Fasce F24 salvate');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Errore salvataggio fasce F24');
    } finally { setSaving(false); }
  };

  const openPrezzo = async (row) => {
    setPrezzo({
    id: row.id,
    tipologia_fatturazione: row.tipologia_fatturazione,
    cliente: row.ragione_sociale,
    fisso: Boolean(row.importo_forzato),
    importo: row.importo_forzato ? Number(row.importo_base || 0) : Number(row.importo || 0),
    dipendenti: row.numero_dipendenti,
    meseDipendenti: row.mese_dipendenti,
    fascia: row.fascia_f24,
    importoFascia: row.importo_fascia,
    note: row.nota_admin || "",
    codice: row.codice_univoco_azienda,
    nuova: Boolean(row.is_zero),
    mancaCedolini: row.numero_dipendenti == null,
    cedoliniPresunti: row.cedolini_presunti ? row.numero_dipendenti : "",
      datoPresunto: Boolean(row.cedolini_presunti),
    });
    setStoricoFasce([]);
    setLoadingStorico(true);
    try {
      const response = await api.get(`/billing/forfait-f24/${row.codice_univoco_azienda}/fasce-storico`);
      setStoricoFasce(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore caricamento storico fasce F24");
    } finally {
      setLoadingStorico(false);
    }
  };

  const eliminaVersioneFasce = async (versione) => {
    if (!prezzo?.codice || !versione?.id) return;
    if (!window.confirm(`Eliminare le fasce con decorrenza ${dataIt(versione.decorrenza)}? Da quella data tornerà valida la versione precedente.`)) return;
    setSaving(true);
    try {
      await api.delete(`/billing/forfait-f24/${prezzo.codice}/fasce-storico/${versione.id}`);
      setStoricoFasce((current) => current.filter((item) => item.id !== versione.id));
      toast.success("Versione fasce eliminata: ripristinata la precedente");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore eliminazione versione fasce");
    } finally {
      setSaving(false);
    }
  };

  const savePrezzo = async () => {
    if (prezzoClosed) { toast.info("Sezione chiusa in Fatturazione"); return; }
    if (!prezzo) return;
    if (!prezzo.fisso && prezzo.mancaCedolini && (!prezzo.cedoliniPresunti || Number(prezzo.cedoliniPresunti) <= 0)) {
      toast.error("Inserisci i cedolini presunti per configurare il forfait F24");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        fisso: prezzo.fisso,
        importo: prezzo.importo,
        note: prezzo.note,
        anno,
        mese,
        cedolini_presunti: prezzo.fisso ? null : (prezzo.cedoliniPresunti || null),
      };
      if (prezzo.nuova) {
        await api.post(`/billing/forfait-f24/${prezzo.codice}/prezzo`, payload);
      } else {
        await api.put(`/billing/forfait-f24/${prezzo.id}/prezzo`, payload);
      }
      toast.success(prezzo.nuova
        ? "Forfait F24 configurato"
        : prezzo.fisso ? "Prezzo fisso salvato" : "Calcolo a fascia ripristinato");
      setPrezzo(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore salvataggio prezzo F24");
    } finally {
      setSaving(false);
    }
  };

  const removePrezzo = async () => {
    if (!prezzo?.id || prezzo.nuova) return;
    if (!window.confirm(`Rimuovere la configurazione F24 di ${prezzo.cliente}? Il cliente resterà visibile tra quelli a zero.`)) return;
    setSaving(true);
    try {
      await api.delete(`/billing/forfait-f24/${prezzo.id}/prezzo`);
      toast.success("Configurazione F24 rimossa");
      setPrezzo(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore rimozione configurazione F24");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!exportRange) return;
    if (!Number.isInteger(exportRange.da) || !Number.isInteger(exportRange.a) || exportRange.da > exportRange.a) {
      toast.error("Intervallo anni non valido");
      return;
    }
    setExporting(true);
    try {
      const response = await api.get("/billing/forfait-f24/export", {
        params: { anno_da: exportRange.da, anno_a: exportRange.a },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `forfait_f24_${exportRange.da}_${exportRange.a}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      setExportRange(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore esportazione Forfait F24");
    } finally {
      setExporting(false);
    }
  };

  const righe = useMemo(() => [
    ...gennaio.map((row) => ({
      ...row,
      codice_prestazione: codiceF24(row),
      prestazione: "Canone annuale per invio F24 telematici",
      tipo_importo: row.importo_forzato ? "Fisso" : `A fascia · ${row.fascia_f24 || "Fino a 5"}`,
      totale: Number(row.importo || 0),
      settore: "F24",
    })),
    ...prorata.map((row) => ({
      ...row,
      codice_prestazione: codiceF24(row),
      prestazione: "Canone annuale per invio F24 telematici",
      tipo_importo: row.importo_forzato ? "Fisso" : `A fascia · ${row.fascia_f24 || "Fino a 5"}`,
      totale: Number(row.importo || 0),
      settore: "F24",
      nota_visualizzata: `Pro-rata ${row.mesi_prorata || 14 - mese}/12${row.nota_admin ? ` — ${row.nota_admin}` : ""}`,
      is_prorata: true,
    })),
  ], [gennaio, prorata, mese]);

  const righeVisibili = useMemo(() => {
    let rows = soloProrata ? righe.filter((row) => row.is_prorata) : righe;
    if (!soloProrata && mostraZero) {
      const presenti = new Set(righe.map((row) => String(row.codice_univoco_azienda)));
      const aZero = incarichiAnno
        .filter((cliente) => !presenti.has(String(cliente.codice_univoco)))
        .map((cliente, index) => ({
        _id: `zero_${cliente.codice_univoco}_${index}`,
        codice_univoco_azienda: cliente.codice_univoco,
        ragione_sociale: cliente.ragione_sociale,
        tipologia_fatturazione: cliente.tipologia_fatturazione,
        codice_prestazione: codiceF24(cliente),
        prestazione: "Canone annuale per invio F24 telematici",
        tipo_importo: "Non configurato",
        totale: 0,
        settore: "F24",
        nota_visualizzata: "F24 attivo nell'anno",
        data_inizio_incarico: cliente.data_inizio_incarico,
        data_fine_incarico: cliente.data_fine_incarico,
        numero_dipendenti: cliente.numero_dipendenti,
        mese_dipendenti: cliente.mese_dipendenti,
        fascia_f24: cliente.fascia_f24,
        importo_fascia: cliente.importo_fascia,
        cedolini_presunti: cliente.cedolini_presunti,
        is_zero: true,
        }));
      rows = [...righe, ...aZero];
    }
    return tipologiaFiltro.size > 0
      ? rows.filter((row) => tipologiaFiltro.has(row.tipologia_fatturazione))
      : rows;
  }, [righe, incarichiAnno, mostraZero, soloProrata, tipologiaFiltro]);

  const totaleVisibile = useMemo(
    () => righeVisibili.reduce((somma, row) => somma + Number(row.totale || 0), 0),
    [righeVisibili]
  );

  const totaliPerTipologia = useMemo(() => righeVisibili.reduce((acc, row) => {
    const tipo = row.tipologia_fatturazione || "Altro";
    acc[tipo] = (acc[tipo] || 0) + Number(row.totale || 0);
    return acc;
  }, {}), [righeVisibili]);

  const baseColumns = [
    {
      field: "tipologia_fatturazione",
      headerName: "Tipo",
      width: 62,
      renderCell: ({ value }) => <span className={`rounded px-2 py-0.5 text-xs ${value === "B" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{value}</span>,
    },
    { field: "ragione_sociale", headerName: "Cliente", flex: 0.9, minWidth: 250 },
    { field: "codice_prestazione", headerName: "Cod", width: 90 },
    { field: "prestazione", headerName: "Prestazione", flex: 1.25, minWidth: 330 },
    {
      field: "tipo_importo",
      headerName: "Tipo prezzo",
      width: 165,
      headerAlign: "center",
      align: "center",
      renderCell: ({ value }) => (
        <span className={`rounded border px-2 py-0.5 text-xs font-semibold ${
          value?.startsWith("A fascia")
            ? "border-blue-200 bg-blue-50 text-blue-700"
            : "border-gray-200 bg-gray-50 text-gray-600"
        }`}>
          {value}
        </span>
      ),
    },
    {
      field: "totale",
      headerName: "Totale",
      width: 150,
      type: "number",
      renderCell: ({ value }) => <span className="font-semibold">{euro(value)}</span>,
    },
    { field: "settore", headerName: "Settore", width: 130 },
    {
      field: "data_inizio_incarico",
      headerName: "Inizio incarico",
      width: 130,
      valueFormatter: ({ value }) => dataIt(value),
    },
    {
      field: "data_fine_incarico",
      headerName: "Fine incarico",
      width: 130,
      valueFormatter: ({ value }) => dataIt(value),
    },
    {
      field: "nota_visualizzata",
      headerName: "Note",
      flex: 0.65,
      minWidth: 150,
      valueGetter: ({ row }) => row.nota_visualizzata || row.nota_admin || "—",
    },
  ];

  const gridProps = {
    density: "compact",
    getRowId: (row) => row._id,
    loading,
    disableRowSelectionOnClick: true,
    localeText: itIT.components.MuiDataGrid.defaultProps.localeText,
    slots: { toolbar: Toolbar, loadingOverlay: LinearProgress, footer: CustomFooterPersonalized },
    getRowClassName: ({ row }) => `${row.cristallizzato ? "" : "cursor-pointer"} ${row.is_prorata ? "bg-blue-50" : ""}`,
    onRowClick: ({ row }) => { if (!roleReadOnly && !row.cristallizzato && !(row.tipologia_fatturazione === 'B' ? closure.bpointClosed : closure.ziClosed)) openPrezzo(row); },
  };

  return (
    <div className={embedded ? "w-full" : "mx-auto w-full page-container gap-4"}>
      <div className="el-container space-y-4 p-4">
        <div>
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-xl font-semibold text-gray-800">Forfait F24</h1>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <div className="flex flex-wrap items-center justify-end gap-2"><BillingClosureBadges state={closure} /></div>
            {!roleReadOnly && <button type="button" onClick={openFasceEditor} className="rounded border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700">Gestisci fasce</button>}
            </div>
          </div>
          <p className="text-sm text-gray-500">
            A gennaio sono fatturati i canoni annuali; negli altri mesi sono mostrati esclusivamente i pro-rata dei nuovi clienti.
          </p>
        </div>

        <div className="flex min-h-8 flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600">
          <span className="leading-6"><strong>{righeVisibili.length}</strong> clienti visualizzati</span>
          <span className="leading-6">Periodo: <strong>{String(mese).padStart(2, "0")}/{anno}</strong></span>
          {prorata.length > 0 && (
            <button
              type="button"
              onClick={() => setSoloProrata((value) => !value)}
              className={`inline-flex h-7 items-center rounded border px-2.5 text-xs font-semibold transition-colors ${
                soloProrata
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
              }`}
            >
              {prorata.length} pro-rata del mese
            </button>
          )}
          <button
            type="button"
            onClick={() => setExportRange({ da: anno, a: anno })}
            className={`ml-auto ${EXPORT_BUTTON_CLASS}`}
          >
            Esporta Excel
          </button>
          <label className="flex h-7 cursor-pointer items-center gap-2 text-gray-700">
            <input
              type="checkbox"
              checked={mostraZero}
              onChange={(event) => setMostraZero(event.target.checked)}
            />
            Mostra anche a zero
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-xs font-semibold uppercase text-gray-500">Gestionale</span>
          {[
            { id: "Z", label: "Z", active: "border-blue-400 bg-blue-100 text-blue-700 ring-1 ring-blue-300", idle: "border-gray-200 bg-white text-gray-500 hover:bg-gray-50" },
            { id: "I", label: "I", active: "border-green-400 bg-green-100 text-green-700 ring-1 ring-green-300", idle: "border-gray-200 bg-white text-gray-500 hover:bg-gray-50" },
            { id: "B", label: "B", active: "border-purple-400 bg-purple-100 text-purple-700 ring-1 ring-purple-300", idle: "border-gray-200 bg-white text-gray-500 hover:bg-gray-50" },
          ].map((filtro) => (
            <button
              key={filtro.id || "tutti"}
              type="button"
              onClick={() => setTipologiaFiltro((current) => { const next = new Set(current); next.has(filtro.id) ? next.delete(filtro.id) : next.add(filtro.id); return next; })}
              className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${
                tipologiaFiltro.has(filtro.id)
                  ? filtro.active
                  : filtro.idle
              }`}
            >
              {filtro.label}
            </button>
          ))}
        </div>

        <TotalSectionCard
          total={totaleVisibile}
          totalsByType={totaliPerTipologia}
          onClick={() => setTotaliOpen(true)}
        />

        <div style={{ height: "calc(100vh - 140px)", minHeight: 620 }}>
          <DataGridPro rows={righeVisibili} columns={baseColumns} {...gridProps} />
        </div>

        <TotaliPrestazioniModal
          isOpen={totaliOpen}
          onClose={() => setTotaliOpen(false)}
          subtitle={`Forfait F24 ${String(mese).padStart(2, "0")}/${anno}`}
          rows={righeVisibili.map((row) => ({
            tipo: row.tipologia_fatturazione || "Z",
            codice: row.codice_prestazione,
            descrizione: row.prestazione,
            quantita: 1,
            totale: row.totale,
          }))}
        />

        {prezzo && (
          <Modal isOpen size="small" closeButton={() => setPrezzo(null)} headerText={`Forfait F24 — ${prezzo.cliente}`}>
            <div className="space-y-4 p-4 text-sm">
              <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-gray-700">
                <div>Dipendenti considerati: <strong>{prezzo.dipendenti ?? (prezzo.cedoliniPresunti || 0)}</strong>{prezzo.datoPresunto ? " (presunti)" : ""}</div>
                <div>
                  Mese dati cedolini: <strong>{prezzo.meseDipendenti
                    ? `${String(prezzo.meseDipendenti.mese).padStart(2, "0")}/${prezzo.meseDipendenti.anno}`
                    : "Nessun dato disponibile"}</strong>
                </div>
                <div>Fascia: <strong>{prezzo.fascia || "Fino a 5"}</strong></div>
                <div>Importo a fascia: <strong>{euro(prezzo.importoFascia ?? 120)}</strong></div>
              </div>
              {prezzo.mancaCedolini && !prezzo.fisso && (
                <label className="block">
                  <span className="mb-1 block text-gray-600">Cedolini presunti</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={prezzo.cedoliniPresunti}
                    onChange={(event) => {
                      const value = event.target.value;
                      const tariffa = f24Tier(value, fasceF24);
                      setPrezzo((current) => ({
                        ...current,
                        cedoliniPresunti: value,
                        dipendenti: value === "" ? null : Number(value),
                        meseDipendenti: value === ""
                          ? null
                          : (mese === 1 ? { anno: anno - 1, mese: 12 } : { anno, mese: mese - 1 }),
                        fascia: tariffa.fascia,
                        importoFascia: tariffa.importo,
                        datoPresunto: value !== "",
                      }));
                    }}
                    className="w-full rounded border px-3 py-2"
                    placeholder="Inserisci una stima..."
                  />
                  <span className="mt-1 block text-xs text-gray-500">Sarà sostituito automaticamente dal primo dato reale disponibile.</span>
                </label>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={prezzo.fisso}
                  onChange={(event) => setPrezzo((current) => ({ ...current, fisso: event.target.checked }))}
                />
                Usa un prezzo fisso invece dell'importo a fascia
              </label>
              <label className="block">
                <span className="mb-1 block text-gray-600">Prezzo fisso €</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={prezzo.importo}
                  disabled={!prezzo.fisso}
                  onChange={(event) => setPrezzo((current) => ({ ...current, importo: event.target.value }))}
                  className="w-full rounded border px-3 py-2 disabled:bg-gray-100 disabled:text-gray-400"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-gray-600">Note</span>
                <textarea
                  rows={3}
                  value={prezzo.note}
                  onChange={(event) => setPrezzo((current) => ({ ...current, note: event.target.value }))}
                  className="w-full resize-y rounded border px-3 py-2"
                  placeholder="Note sul forfait F24..."
                />
              </label>
              <div className="border-t pt-3">
                <div className="mb-2 font-medium text-gray-700">Storico fasce</div>
                {loadingStorico ? (
                  <div className="text-gray-500">Caricamento...</div>
                ) : storicoFasce.length === 0 ? (
                  <div className="text-gray-500">Nessuna variazione registrata. Valori base: 120 / 240 / 360 / 480 €.</div>
                ) : (
                  <div className="space-y-2">
                    {storicoFasce.map((versione) => (
                      <div key={versione.id} className="rounded border bg-gray-50 p-2">
                        <div className="flex items-center justify-between gap-2">
                          <strong>Dal {dataIt(versione.decorrenza)}</strong>
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => eliminaVersioneFasce(versione)}
                            className="rounded border border-red-200 bg-white px-2 py-1 text-xs text-red-700 disabled:opacity-50"
                          >
                            Elimina versione
                          </button>
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          Fino a 5: {euro(versione.importo_fino_5)} · 6-15: {euro(versione.importo_6_15)} · 16-50: {euro(versione.importo_16_50)} · Oltre 50: {euro(versione.importo_oltre_50)}
                        </div>
                        {prezzo?.tipologia_fatturazione !== "B" && versione.percentuale_istat != null && (
                          <div className="mt-1 text-xs text-gray-500">ISTAT {formatItalianNumber(versione.percentuale_istat, { maximumFractionDigits: 2 })}%{versione.modificato_da ? ` · ${versione.modificato_da}` : ""}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button disabled={saving || prezzoClosed} onClick={savePrezzo} className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50">
                  {saving ? "Salvataggio..." : "Salva"}
                </button>
                <button onClick={() => setPrezzo(null)} className="rounded border px-4 py-2">Annulla</button>
                {!prezzo.nuova && (
                  <button
                    onClick={removePrezzo}
                    disabled={saving}
                    className="ml-auto rounded border border-red-200 bg-red-50 px-4 py-2 text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    Rimuovi configurazione
                  </button>
                )}
              </div>
            </div>
          </Modal>
        )}

        {fasceEditor && (
          <Modal isOpen size="medium" disableOverlayClose closeButton={() => setFasceEditor(null)} headerText="Gestisci fasce Forfait F24">
            <div className="space-y-4 p-4">
              <label className="block text-sm">Decorrenza
                <input type="date" value={fasceEditor.decorrenza} onChange={(e) => setFasceEditor((v) => ({ ...v, decorrenza: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5" />
              </label>
              <div className="space-y-2">
                {fasceEditor.fasce.map((fascia, index) => (
                  <div key={index} className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2 rounded border p-2">
                    {[
                      ['min_cedolini', 'Da cedolini', '0'],
                      ['max_cedolini', 'A cedolini', 'Senza limite'],
                      ['importo', 'Importo €', '0,00'],
                    ].map(([field, label, placeholder]) => (
                      <label key={field} className="text-xs">{label}
                        <input type="number" min="0" step={field === 'importo' ? '0.01' : '1'} value={fascia[field]} placeholder={placeholder}
                          onChange={(e) => setFasceEditor((v) => ({ ...v, fasce: v.fasce.map((f, i) => i === index ? { ...f, [field]: e.target.value } : f) }))}
                          className="mt-1 w-full rounded border px-2 py-1.5" />
                      </label>
                    ))}
                    <button type="button" disabled={fasceEditor.fasce.length === 1}
                      onClick={() => setFasceEditor((v) => ({ ...v, fasce: v.fasce.filter((_, i) => i !== index) }))}
                      className="rounded border border-red-200 px-2 py-1.5 text-sm text-red-700 disabled:opacity-40">Rimuovi</button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setFasceEditor((v) => ({ ...v, fasce: [...v.fasce, { min_cedolini: '', max_cedolini: '', importo: '' }] }))}
                className="rounded border border-green-300 bg-green-50 px-3 py-1.5 text-sm text-green-700">Aggiungi fascia</button>
              <div className="flex gap-2">
                <button type="button" disabled={saving} onClick={salvaFasce} className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50">Salva fasce</button>
                <button type="button" onClick={() => setFasceEditor(null)} className="rounded border px-4 py-2">Annulla</button>
              </div>
            </div>
          </Modal>
        )}

        {exportRange && (
          <Modal isOpen size="small" closeButton={() => setExportRange(null)} headerText="Esporta Forfait F24">
            <div className="space-y-4 p-4">
              <p className="text-sm text-gray-500">
                Verrà creato un foglio per ogni anno con incarichi, configurazione economica,
                importi mensili, pro-rata e dettaglio dei cedolini e della fascia applicata.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">Da (anno)
                  <input type="number" value={exportRange.da}
                    onChange={(event) => setExportRange((value) => ({ ...value, da: Number(event.target.value) }))}
                    className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
                <label className="text-sm">A (anno)
                  <input type="number" value={exportRange.a}
                    onChange={(event) => setExportRange((value) => ({ ...value, a: Number(event.target.value) }))}
                    className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
              </div>
              <div className="flex gap-2">
                <button onClick={handleExport} disabled={exporting} className={EXPORT_BUTTON_CLASS}>
                  {exporting ? "Esportazione..." : "Esporta Excel"}
                </button>
                <button onClick={() => setExportRange(null)} className="rounded border px-4 py-2 text-sm">Annulla</button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};
